import {
  type FC,
  type PropsWithChildren,
  createContext,
  useCallback,
  useMemo,
  useState,
} from 'react';

/**
 * Returns a boolean "isPrintable" and a setter method for updating it.
 * Calling `setIsPrintable()` will update both the state AND the CSS class on the <html> element.
 * We apply a CSS class 'is-printable' whenever the state is true. This CSS class can then
 * be used to override some styles for printing only. Specifically, we configure tailwind CSS
 * dark mode such that it doesn't apply if the <html> element has the 'is-printable' class
 * (even if it does have the 'dark' class).
 */
export const useIsPrintable = (
  initialPathname: string,
  initialState: boolean | null | undefined,
  containerElement: HTMLElement | null | undefined
) => {
  initialState = !!initialState;
  containerElement = containerElement || document?.documentElement;

  const [isPrintable, _setIsPrintable] = useState<boolean>(initialState);
  const [pathname, setPathname] = useState(initialPathname ?? '');
  const setIsPrintable = useCallback((isPrintable: boolean) => {
    _setIsPrintable(isPrintable);
    if (isPrintable) {
      containerElement?.classList.add('is-printable');
    } else {
      containerElement?.classList.remove('is-printable');
    }
  }, []);
  return useMemo(
    () => ({ pathname, isPrintable, setIsPrintable }),
    [pathname, setPathname, isPrintable, setIsPrintable]
  );
};

export type PrintableContextValueType = ReturnType<typeof useIsPrintable>;

export const PrintableContext = createContext<PrintableContextValueType | undefined>(undefined);

type PrintableProviderProps = PropsWithChildren & {
  initialPathname: string;
  initialState?: boolean | null;
  containerElement?: HTMLElement | null;
};

/** Exposes the useIsPrintable hook state. */
export const PrintableProvider: FC<PrintableProviderProps> = ({
  initialPathname,
  initialState,
  containerElement,
  children,
}) => {
  const value = useIsPrintable(initialPathname, initialState, containerElement);
  return <PrintableContext value={value}>{children}</PrintableContext>;
};

PrintableProvider.displayName = 'PrintableProvider';
